import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from './cn';

export interface AutoGrowTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Minimaal aantal zichtbare regels (standaard 6). */
  minRows?: number;
  /** Vanaf dit aantal regels komt er een interne scrollbalk (standaard 30). */
  maxRows?: number;
}

/**
 * Tekstvak dat met de inhoud meegroeit: nooit kleiner dan `minRows`, geen
 * interne scrollbalk tot `maxRows`. Volle breedte, ruime regelafstand, zodat
 * langere teksten (uitleg, reacties, notities) prettig lezen en typen.
 */
const AutoGrowTextarea = forwardRef<HTMLTextAreaElement, AutoGrowTextareaProps>(
  function AutoGrowTextarea(
    { className, minRows = 6, maxRows = 30, value, onChange, ...props },
    ref,
  ) {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

    const pasHoogteAan = useCallback(() => {
      const el = innerRef.current;
      if (!el) return;
      const stijl = window.getComputedStyle(el);
      const regel = parseFloat(stijl.lineHeight) || 24;
      const padding = (parseFloat(stijl.paddingTop) || 0) + (parseFloat(stijl.paddingBottom) || 0);
      const rand =
        (parseFloat(stijl.borderTopWidth) || 0) + (parseFloat(stijl.borderBottomWidth) || 0);
      // box-sizing: border-box → hoogte = regels + padding + rand.
      const min = minRows * regel + padding + rand;
      const max = maxRows * regel + padding + rand;
      el.style.height = 'auto';
      // scrollHeight is inclusief padding, exclusief rand.
      const gewenst = el.scrollHeight + rand;
      const hoogte = Math.min(Math.max(gewenst, min), max);
      el.style.height = `${hoogte}px`;
      el.style.overflowY = gewenst > max ? 'auto' : 'hidden';
    }, [minRows, maxRows]);

    useLayoutEffect(() => {
      pasHoogteAan();
    }, [pasHoogteAan, value]);

    useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el || typeof ResizeObserver === 'undefined') return;
      const ro = new ResizeObserver(() => pasHoogteAan());
      ro.observe(el);
      return () => ro.disconnect();
    }, [pasHoogteAan]);

    return (
      <textarea
        ref={innerRef}
        rows={minRows}
        value={value}
        onChange={(e) => {
          onChange?.(e);
          pasHoogteAan();
        }}
        className={cn(
          'block w-full resize-none rounded-xl border border-theme-border-strong bg-surface px-3.5 py-3',
          'text-[15px] leading-relaxed text-foreground placeholder:text-foreground-tertiary',
          'focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      />
    );
  },
);

export default AutoGrowTextarea;
